import React from 'react';
import { colors, spacing } from '../../styles/designTokens';

/**
 * LoadingSpinner Component
 * 
 * Standardized loading indicator used throughout the application.
 * Replaces inconsistent loading spinners with a single, reusable component.
 * 
 * Usage:
 * <LoadingSpinner size="large" message="Loading dashboard data..." />
 * <LoadingSpinner size="small" />
 * <LoadingSpinner size="medium" message="Saving..." inline />
 * 
 * Props:
 * - size: 'small' | 'medium' | 'large' (default: 'medium')
 * - message: Optional message to display below spinner
 * - inline: Boolean - if true, displays horizontally (for inline use)
 * - color: Optional custom color (defaults to primary blue)
 */

const LoadingSpinner = ({ 
  size = 'medium', 
  message = '', 
  inline = false,
  color = colors.primary.main 
}) => {
  // Size configurations
  const sizeConfig = {
    small: {
      spinner: 'w-4 h-4 border-2',
      text: 'text-xs',
      gap: 'gap-2'
    },
    medium: {
      spinner: 'w-8 h-8 border-2',
      text: 'text-sm',
      gap: 'gap-3'
    },
    large: {
      spinner: 'w-12 h-12 border-4',
      text: 'text-base',
      gap: 'gap-4'
    }
  };

  const config = sizeConfig[size] || sizeConfig.medium;
  
  // Container layout: inline (flex-row) or stacked (flex-col)
  const containerClasses = inline 
    ? `flex flex-row items-center ${config.gap}`
    : `flex flex-col items-center justify-center ${config.gap}`;

  return (
    <div 
      className={containerClasses}
      role="status"
      aria-live="polite"
      aria-label={message || 'Loading'}
    >
      {/* Spinner */}
      <div
        className={`${config.spinner} border-t-transparent rounded-full animate-spin`}
        style={{
          borderColor: `${color}40`, // 40 is hex for ~25% opacity
          borderTopColor: color
        }}
        aria-hidden="true"
      />
      
      {/* Optional message */}
      {message && (
        <p 
          className={`${config.text} text-gray-600 font-medium`}
          style={{ margin: 0 }}
        >
          {message}
        </p>
      )}
      
      {/* Screen reader only text */}
      <span className="sr-only">
        {message || 'Loading, please wait...'}
      </span>
    </div>
  );
};

/**
 * LoadingOverlay Component
 * 
 * Full-screen or container-based loading overlay
 * 
 * Usage:
 * <LoadingOverlay message="Loading user data..." />
 * <LoadingOverlay message="Saving changes..." fullScreen={false} />
 */
export const LoadingOverlay = ({ 
  message = 'Loading...', 
  fullScreen = true,
  transparent = false 
}) => {
  const overlayClasses = fullScreen
    ? 'fixed inset-0 z-50'
    : 'absolute inset-0 z-10';

  const bgColor = transparent
    ? 'bg-white/70 backdrop-blur-sm'
    : 'bg-white';

  return (
    <div 
      className={`${overlayClasses} ${bgColor} flex items-center justify-center`}
      role="status"
      aria-live="polite"
      aria-label={message}
    >
      <div className="text-center">
        <LoadingSpinner size="large" message={message} />
      </div>
    </div>
  );
};

/**
 * LoadingButton Component
 * 
 * Button with integrated loading spinner
 * 
 * Usage:
 * <LoadingButton loading={isLoading} onClick={handleSave}>
 *   Save Changes
 * </LoadingButton>
 */
export const LoadingButton = ({ 
  loading = false, 
  children, 
  disabled = false,
  ...props 
}) => {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      className={`relative ${props.className || ''}`}
    >
      {/* Show spinner when loading */}
      {loading && (
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <LoadingSpinner size="small" inline />
        </span>
      )}
      
      {/* Content - hidden when loading */}
      <span className={loading ? 'invisible' : ''}>
        {children}
      </span>
    </button>
  );
};

/**
 * LoadingCard Component
 * 
 * Skeleton loading card for better perceived performance
 * 
 * Usage:
 * <LoadingCard height="200px" />
 */
export const LoadingCard = ({ height = '150px', className = '' }) => {
  return (
    <div 
      className={`bg-gray-100 rounded-lg animate-pulse ${className}`}
      style={{ height }}
      role="status"
      aria-label="Loading content"
    >
      <span className="sr-only">Loading content...</span>
    </div>
  );
};

/**
 * LoadingTable Component
 * 
 * Skeleton loading for tables
 * 
 * Usage:
 * <LoadingTable rows={5} columns={4} />
 */
export const LoadingTable = ({ rows = 5, columns = 3 }) => {
  return (
    <div className="space-y-3" role="status" aria-label="Loading table data">
      {/* Header row */}
      <div className="flex gap-4">
        {Array.from({ length: columns }).map((_, i) => (
          <div 
            key={`header-${i}`}
            className="h-8 bg-gray-200 rounded flex-1 animate-pulse"
          />
        ))}
      </div>
      
      {/* Data rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={`row-${rowIndex}`} className="flex gap-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div 
              key={`cell-${rowIndex}-${colIndex}`}
              className="h-12 bg-gray-100 rounded flex-1 animate-pulse"
              style={{
                animationDelay: `${(rowIndex * columns + colIndex) * 50}ms`
              }}
            />
          ))}
        </div>
      ))}
      
      <span className="sr-only">Loading table data...</span>
    </div>
  );
};

export default LoadingSpinner;

